import { DateTime } from 'luxon';
import { YardTaskStatus } from '@nova/core';
import { get } from 'lodash';

import {
  mapChecklist,
  mapRelatedEntity,
  mapTags,
  mapDueDateTime,
  isCheckTask,
  isMoveTask
} from './EventTriggeredCreateTaskDrawer.helpers';

import type {
  CheckTaskEditData,
  CheckTaskApiRequestData,
  CheckTaskApiResponseData,
  MoveTaskEditData,
  MoveTaskApiRequestData,
  MoveTaskApiResponseData,
  TimeInput
} from '@nova/core';

export interface DisabledFieldsContext {
  taskStatus: string;
  taskType: 'move' | 'check';
}

/**
 * Creates a disabled fields configuration based on task status, type, and user permissions
 */
export function createDisabledFieldsConfig(context: DisabledFieldsContext) {
  const { taskStatus } = context;

  switch (taskStatus) {
    case YardTaskStatus.COMPLETED:
    case YardTaskStatus.CANCELLED:
      return {
        description: false,
        attachments: false,
        assignee: true,
        dueByDate: true,
        dueByTime: true,
        isUrgent: true
      };

    case YardTaskStatus.IN_PROGRESS:
      return {
        description: false,
        attachments: false,
        assignee: true,
        dueByDate: false,
        dueByTime: false,
        isUrgent: false
      };

    case YardTaskStatus.CREATED:
    default:
      return {
        description: false,
        attachments: false,
        assignee: false,
        dueByDate: false,
        dueByTime: false,
        isUrgent: false
      };
  }
}

/**
 * Creates a disabled fields configuration specifically for move tasks
 */
export function createMoveTaskDisabledFields(context: DisabledFieldsContext) {
  const baseConfig = createDisabledFieldsConfig(context);

  switch (context.taskStatus) {
    case YardTaskStatus.COMPLETED:
    case YardTaskStatus.CANCELLED:
    case YardTaskStatus.IN_PROGRESS:
      return {
        ...baseConfig,
        asset: true,
        originSpot: true,
        destinationSpot: true
      };

    case YardTaskStatus.CREATED:
    default:
      return {
        ...baseConfig,
        asset: false,
        originSpot: false,
        destinationSpot: false
      };
  }
}

/**
 * Creates a disabled fields configuration specifically for check tasks
 */
export function createCheckTaskDisabledFields(context: DisabledFieldsContext) {
  const baseConfig = createDisabledFieldsConfig(context);

  switch (context.taskStatus) {
    case YardTaskStatus.COMPLETED:
    case YardTaskStatus.CANCELLED:
      return {
        ...baseConfig,
        subject: true,
        spot: true,
        area: true,
        asset: true,
        category: false,
        checklist: true
      };

    case YardTaskStatus.IN_PROGRESS:
      return {
        ...baseConfig,
        subject: true,
        spot: true,
        area: true,
        asset: true,
        category: false,
        checklist: false
      };

    case YardTaskStatus.CREATED:
    default:
      return {
        ...baseConfig,
        subject: false,
        spot: false,
        area: false,
        asset: false,
        category: false,
        checklist: false
      };
  }
}

export function createTaskDisabledFields(context: DisabledFieldsContext) {
  if (context.taskType === 'move') {
    return createMoveTaskDisabledFields(context);
  } else if (context.taskType === 'check') {
    return createCheckTaskDisabledFields(context);
  }

  return {};
}

export function hasCheckTaskChanged(
  currentTaskData: CheckTaskEditData,
  fetchedTaskData: CheckTaskEditData
) {
  const currChecklist = Array.isArray(currentTaskData.checklist) ? currentTaskData.checklist : [];
  const prevChecklist = Array.isArray(fetchedTaskData.checklist) ? fetchedTaskData.checklist : [];
  const checklistChanges =
    currChecklist.length !== prevChecklist.length ||
    currChecklist.some((item, index) => item.description !== prevChecklist[index]?.description);

  const dueByTimeChanges =
    currentTaskData.dueByTime?.amPm !== fetchedTaskData.dueByTime?.amPm ||
    currentTaskData.dueByTime?.time !== fetchedTaskData.dueByTime?.time;
  const dueByDateChanges = currentTaskData.dueByDate !== fetchedTaskData.dueByDate;
  const isUrgentChanges = currentTaskData.isUrgent !== fetchedTaskData.isUrgent;
  const assigneeChanges = currentTaskData.assignee !== fetchedTaskData.assignee;
  const assetChanges = currentTaskData.asset !== fetchedTaskData.asset;
  const areaChanges = currentTaskData.area !== fetchedTaskData.area;
  const spotChanges = currentTaskData.spot !== fetchedTaskData.spot;
  const subjectChanges = currentTaskData.subject !== fetchedTaskData.subject;
  const categoryChanges = currentTaskData.category !== fetchedTaskData.category;
  const descriptionChanges = currentTaskData.description !== fetchedTaskData.description;

  return (
    checklistChanges ||
    dueByTimeChanges ||
    dueByDateChanges ||
    isUrgentChanges ||
    assigneeChanges ||
    assetChanges ||
    areaChanges ||
    spotChanges ||
    subjectChanges ||
    categoryChanges ||
    descriptionChanges
  );
}

export function hasMoveTaskChanged(
  currentTaskData: MoveTaskEditData,
  fetchedTaskData: MoveTaskEditData
) {
  const assetChanges = currentTaskData.asset !== fetchedTaskData.asset;
  const originSpotChanges = currentTaskData.originSpot != fetchedTaskData.originSpot;
  const destinationSpotChanges = currentTaskData.destinationSpot != fetchedTaskData.destinationSpot;
  const descriptionChanges = currentTaskData.description !== fetchedTaskData.description;
  const dueByDateChanges = currentTaskData.dueByDate !== fetchedTaskData.dueByDate;
  const dueByTimeChanges =
    currentTaskData.dueByTime?.amPm !== fetchedTaskData.dueByTime?.amPm ||
    currentTaskData.dueByTime?.time !== fetchedTaskData.dueByTime?.time;
  const isUrgentChanges = currentTaskData.isUrgent !== fetchedTaskData.isUrgent;
  const assigneeChanges = currentTaskData.assignee !== fetchedTaskData.assignee;

  return (
    assetChanges ||
    originSpotChanges ||
    destinationSpotChanges ||
    dueByDateChanges ||
    dueByTimeChanges ||
    isUrgentChanges ||
    assigneeChanges ||
    descriptionChanges
  );
}

export function hasTaskChanged(
  currentTaskData: CheckTaskEditData | MoveTaskEditData,
  fetchedTaskData: CheckTaskEditData | MoveTaskEditData
) {
  if (isCheckTask(currentTaskData) && isCheckTask(fetchedTaskData)) {
    return hasCheckTaskChanged(currentTaskData, fetchedTaskData);
  } else if (isMoveTask(currentTaskData) && isMoveTask(fetchedTaskData)) {
    return hasMoveTaskChanged(currentTaskData, fetchedTaskData);
  }

  return false;
}

/**
 * Transforms due date time from API format to form state format
 * @param dueDateTime - ISO date string from API
 * @param warehouseTimezone - Warehouse timezone
 * @returns Object with dueByDate and dueByTime
 */
export function transformDueDatePayloadToState(
  dueDateTime: string,
  warehouseTimezone: string
): {
  dueByDate: string | null;
  dueByTime: TimeInput | null;
} {
  if (!dueDateTime) {
    return {
      dueByDate: '',
      dueByTime: {
        time: '',
        amPm: 'pm'
      }
    };
  }

  const date = DateTime.fromISO(dueDateTime).setZone(warehouseTimezone);
  const dueByDate = date.isValid ? date.toFormat('yyyy-MM-dd') : '';
  const dueByTime = {
    time: date.isValid ? date.toFormat('hh:mm') : '',
    amPm: date.isValid ? (date.hour >= 12 ? 'pm' : 'am') : 'pm'
  };

  return {
    dueByDate,
    dueByTime
  };
}

/**
 * Transforms check task API response to form state format
 * @param apiResponse - API response data
 * @param warehouseTimezone - Warehouse timezone
 * @returns Check task form state
 */
export function transformCheckTaskApiResponseDataToEditData(
  apiResponse: CheckTaskApiResponseData,
  warehouseTimezone: string
): CheckTaskEditData {
  const relatedEntity = get(apiResponse, 'child.relatedEntity');
  const relatedId = get(apiResponse, 'child.relatedId');

  return {
    type: 'check',
    id: get(apiResponse, 'id') || null,
    status: get(apiResponse, 'status') || '',
    description: get(apiResponse, 'description') || '',
    order: get(apiResponse, 'order') || null,
    refNum: get(apiResponse, 'refNum') || null,
    createdAt: get(apiResponse, 'createDateTime') || null,
    createdBy: get(apiResponse, 'createdBy') || null,
    category: get(apiResponse, 'child.category') || '',
    subject: relatedEntity || '',
    spot: relatedEntity === 'Spot' ? relatedId || null : null,
    area: relatedEntity === 'SpotArea' ? relatedId || null : null,
    asset: relatedEntity === 'AssetContainer' ? relatedId || null : null,
    assignee: get(apiResponse, 'assignedTo.id') || null,
    ...transformDueDatePayloadToState(apiResponse.dueDateTime, warehouseTimezone),
    isUrgent: (get(apiResponse, 'tags') || []).includes('urgent'),
    checklist: (get(apiResponse, 'child.checklist') || []).map(item => ({
      description: item.description,
      complete: item.complete
    }))
  };
}

/**
 * Transforms move task API response to form state format
 * @param apiResponse - API response data
 * @param warehouseTimezone - Warehouse timezone
 * @returns Move task form state
 */
export function transformMoveTaskApiResponseDataToEditData(
  apiResponse: MoveTaskApiResponseData,
  warehouseTimezone: string
): MoveTaskEditData {
  return {
    type: 'move',
    id: get(apiResponse, 'id') || null,
    status: get(apiResponse, 'status') || '',
    order: get(apiResponse, 'order') || null,
    refNum: get(apiResponse, 'refNum') || null,
    description: get(apiResponse, 'description') || '',
    createdAt: get(apiResponse, 'createDateTime') || null,
    createdBy: get(apiResponse, 'createdBy') || null,
    asset: get(apiResponse, 'child.assetContainer.id') || null,
    originSpot: get(apiResponse, 'child.originSpot.id') || null,
    destinationSpot: get(apiResponse, 'child.destinationSpot.id') || null,
    assignee: get(apiResponse, 'assignedTo.id') || null,
    ...transformDueDatePayloadToState(apiResponse.dueDateTime, warehouseTimezone),
    isUrgent: (get(apiResponse, 'tags') || []).includes('urgent')
  };
}

/**
 * Transforms API response back to form state format
 * @param apiResponse - API response data
 * @param warehouseTimezone - Warehouse timezone
 * @returns Form state object
 */
export function transformApiResponseDataToEditData(
  apiResponse: CheckTaskApiResponseData | MoveTaskApiResponseData,
  warehouseTimezone: string
): CheckTaskEditData | MoveTaskEditData | null {
  if (!apiResponse) {
    console.error('No task data available');
    return null;
  }

  if (isCheckTask(apiResponse)) {
    return transformCheckTaskApiResponseDataToEditData(apiResponse, warehouseTimezone);
  }

  if (isMoveTask(apiResponse)) {
    return transformMoveTaskApiResponseDataToEditData(apiResponse, warehouseTimezone);
  }

  return null;
}

/**
 * Transforms check task form state to edit payload format
 * @param taskData - Form task data
 * @param warehouseId - Warehouse ID
 * @param warehouseTimezone - Warehouse timezone
 * @returns Edit payload or null if invalid
 */
export function transformCheckTaskEditDataToApiRequestData(
  taskData: CheckTaskEditData,
  warehouseId: string,
  warehouseTimezone: string
): Partial<CheckTaskApiRequestData> | null {
  if (!taskData) {
    console.error('No task data available');
    return null;
  }

  const checklist = mapChecklist(taskData.checklist);
  const { relatedEntity, relatedId } = mapRelatedEntity(
    taskData.subject,
    taskData.spot,
    taskData.area,
    taskData.asset
  );

  const tags = mapTags(taskData.isUrgent);
  const dueDateTime = mapDueDateTime(taskData.dueByDate, taskData.dueByTime, warehouseTimezone);

  return {
    warehouseId,
    assignedTo: taskData.assignee || null,
    description: taskData.description || '',
    child: {
      checklist,
      category: taskData.category,
      relatedEntity,
      relatedId
    },
    dueDateTime,
    tags
  };
}

/**
 * Transforms move task form state to edit payload format
 * @param taskData - Form task data
 * @param warehouseId - Warehouse ID
 * @param warehouseTimezone - Warehouse timezone
 * @returns Edit payload or null if invalid
 */
export function transformMoveTaskEditDataToApiRequestData(
  taskData: MoveTaskEditData,
  warehouseId: string,
  warehouseTimezone: string
): Partial<MoveTaskApiRequestData> | null {
  if (!taskData) {
    console.error('No task data available');
    return null;
  }

  const tags = mapTags(taskData.isUrgent);
  const dueDateTime = mapDueDateTime(taskData.dueByDate, taskData.dueByTime, warehouseTimezone);

  return {
    warehouseId,
    assignedTo: taskData.assignee || null,
    description: taskData.description || '',
    child: {
      assetContainerId: taskData.asset ?? null,
      originSpotId: taskData.originSpot ?? null,
      destinationSpotId: taskData.destinationSpot ?? null
    },
    dueDateTime,
    tags
  };
}

/**
 * Transforms form state to edit payload format
 * @param taskData - Form task data
 * @param warehouseId - Warehouse ID
 * @param warehouseTimezone - Warehouse timezone
 * @returns Edit payload or null if invalid
 */
export function transformEditDataToApiRequestData(
  taskData: CheckTaskEditData | MoveTaskEditData | undefined,
  warehouseId: string,
  warehouseTimezone: string
): Partial<CheckTaskApiRequestData> | Partial<MoveTaskApiRequestData> | null {
  if (!taskData) {
    console.error('No task data available');
    return null;
  }

  if (isCheckTask(taskData)) {
    return transformCheckTaskEditDataToApiRequestData(taskData, warehouseId, warehouseTimezone);
  }

  if (isMoveTask(taskData)) {
    return transformMoveTaskEditDataToApiRequestData(taskData, warehouseId, warehouseTimezone);
  }

  return null;
}
